export const REDIRECT_TO_KEY = 'redirect_to';

interface SafeRedirectTarget {
    pathname: string;
}

function parseSafeRedirectTarget(target: string): SafeRedirectTarget | null {
    if (typeof target !== 'string' ||
        target.length === 0 ||
        /[\u0000-\u001F\u007F\s\\]/.test(target) ||
        target.charAt(0) !== '/' ||
        target.charAt(1) === '/') {
        return null;
    }

    try {
        const url = new URL(target, location.origin);
        if (url.origin !== location.origin) {
            return null;
        }

        const decodedPathname = decodeURIComponent(url.pathname);
        if (/[\u0000-\u001F\u007F\s\\]/.test(decodedPathname) ||
            decodedPathname.charAt(0) !== '/' ||
            decodedPathname.charAt(1) === '/') {
            return null;
        }

        return {pathname: decodedPathname};
    } catch (e) {
        return null;
    }
}

export function isSafeRedirectPath(target: string): boolean {
    return parseSafeRedirectTarget(target) !== null;
}

export function isLoginPath(pathname: string): boolean {
    const target = parseSafeRedirectTarget(pathname);
    const normalizedPathname = target === null ? pathname : target.pathname;
    if (typeof normalizedPathname !== 'string') {
        return false;
    }

    return normalizedPathname.split('/').some(segment =>
        segment === 'login' || segment === 'manage-login');
}

export function isRedirectableLocation(pathname: string): boolean {
    const url = parseSafeRedirectTarget(pathname);
    if (url === null || url.pathname === '/') {
        return false;
    }

    const normalizedPathname = url.pathname;
    if (isLoginPath(pathname)) {
        return false;
    }

    const segments = normalizedPathname.split('/').filter(segment => segment.length > 0);
    if (segments.some(segment =>
        segment === 'reset' ||
        segment === 'public' ||
        segment === 'maintenance' ||
        segment === 'twostepauth')) {
        return false;
    }

    return !segments.some((segment, index) =>
        segment === 'user' && segments[index + 1] === 'forgot-password');
}

export function saveRedirectTarget(pathname: string, search: string): void {
    const target = pathname + (search || '');
    const url = parseSafeRedirectTarget(target);
    if (url === null || !isRedirectableLocation(target)) {
        return;
    }

    try {
        localStorage.setItem(REDIRECT_TO_KEY, target);
    } catch (e) {
    }
}

export function readRedirectTarget(): string | null {
    try {
        const target = localStorage.getItem(REDIRECT_TO_KEY);
        if (target === null) {
            return null;
        }
        const url = parseSafeRedirectTarget(target);
        if (url === null || !isRedirectableLocation(target)) {
            localStorage.removeItem(REDIRECT_TO_KEY);
            return null;
        }
        return target;
    } catch (e) {
        return null;
    }
}

export function clearRedirectTarget(): void {
    try {
        localStorage.removeItem(REDIRECT_TO_KEY);
    } catch (e) {
    }
}
